import { useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import iconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png'
import iconUrl from 'leaflet/dist/images/marker-icon.png'
import shadowUrl from 'leaflet/dist/images/marker-shadow.png'
import { getTrip } from '@/services/tripService'
import { listActivitiesByTrip } from '@/services/activityService'

const CATEGORY_COLOR: Record<string, string> = {
  transport: '#c97f4a',
  accommodation: '#6e9aa8',
  discovery: '#c9a15a',
  meal: '#9a8fb0',
}

export function MapPage() {
  const { t } = useTranslation()
  const { tripId } = useParams<{ tripId: string }>()
  const navigate = useNavigate()

  useEffect(() => {
    // Vite bundles Leaflet's default marker images under hashed URLs, which
    // breaks the class's hardcoded relative-path lookup unless overridden.
    delete (L.Icon.Default.prototype as unknown as { _getIconUrl?: unknown })._getIconUrl
    L.Icon.Default.mergeOptions({ iconRetinaUrl, iconUrl, shadowUrl })
  }, [])

  const tripQuery = useQuery({
    queryKey: ['trip', tripId],
    queryFn: () => getTrip(tripId!),
    enabled: !!tripId,
  })

  const activitiesQuery = useQuery({
    queryKey: ['activities', 'trip', tripId],
    queryFn: () => listActivitiesByTrip(tripId!),
    enabled: !!tripId,
  })

  if (tripQuery.isLoading || activitiesQuery.isLoading) {
    return <p className="bg-night p-10 text-mist">{t('map.loading')}</p>
  }

  const trip = tripQuery.data
  const activities = activitiesQuery.data ?? []
  const located = activities.filter(
    (a): a is typeof a & { latitude: number; longitude: number } =>
      a.latitude !== null && a.longitude !== null,
  )

  const center: [number, number] =
    located.length > 0
      ? [
          located.reduce((sum, a) => sum + a.latitude, 0) / located.length,
          located.reduce((sum, a) => sum + a.longitude, 0) / located.length,
        ]
      : [20, 0]

  return (
    <div className="min-h-screen bg-night px-10 py-7">
      <button
        type="button"
        onClick={() => navigate(`/trips/${tripId}/itinerary`)}
        className="mb-3.5 font-sans text-sm text-mist"
      >
        {t('map.backToItinerary')}
      </button>
      <h1 className="font-display text-3xl text-ivory">{t('map.title')}</h1>
      <div className="mt-1.5 font-mono text-xs text-brass-dim">{trip?.title}</div>

      {located.length === 0 ? (
        <p className="mt-8 text-sm text-mist">{t('map.empty')}</p>
      ) : (
        <div className="mt-8 overflow-hidden rounded-lg border border-line">
          <MapContainer
            center={center}
            zoom={located.length > 1 ? 6 : 12}
            style={{ height: '65vh', width: '100%' }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {located.map((activity) => (
              <Marker key={activity.id} position={[activity.latitude, activity.longitude]}>
                <Popup>
                  <strong>{activity.title}</strong>
                  {activity.category && (
                    <div style={{ color: CATEGORY_COLOR[activity.category] }}>
                      {t(`itinerary.category.${activity.category}`)}
                    </div>
                  )}
                  {activity.location && <div>{activity.location}</div>}
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      )}

      {activities.length > located.length && (
        <p className="mt-4 text-sm text-mist">
          {t('map.unlocated', { count: activities.length - located.length })}
        </p>
      )}
    </div>
  )
}
